'use client';

// @ts-expect-error
import { c as _c } from "react/compiler-runtime";
import type { AddressProps } from '@petsmart-ui/sparky/dist/components/Address/Address';
import React from 'react';
import { clsx } from '@libs/common-next';
import { isLocaleCA } from '@libs/core';
import type { ExperianResponse, ExperianResponseLayout, ValidationDetail } from './types';
import { extractZipCodeFromResponse, extractZipCodeFromResponseLayout } from './utils';
import './ExperianModal.scss';

/**
 * Type guard to determine if the response is of type ExperianResponseLayout.
 *
 * This function checks the presence of the `addresses_formatted` property in the response result,
 * which is unique to the ExperianResponseLayout type.
 *
 * @param response - The ExperianResponse or ExperianResponseLayout object to check.
 * @returns True if the response is of type ExperianResponseLayout, otherwise false.
 */
function isExperianResponseLayoutType(response: ExperianResponse | ExperianResponseLayout): response is ExperianResponseLayout {
  return 'result' in response && 'addresses_formatted' in response.result && Array.isArray(response.result.addresses_formatted);
}
const componentClassname = 'modal-item';
export function ExperianModalItem(t0) {
  const $ = _c(41);
  const {
    addressName,
    street,
    extendedAddress,
    city,
    state,
    postalCode,
    validationDetail,
    experianResponse
  } = t0;
  let t1;
  if ($[0] !== experianResponse || $[1] !== postalCode || $[2] !== validationDetail?.zip_code_corrected) {
    t1 = () => {
      if (!experianResponse || !validationDetail?.zip_code_corrected) {
        return false;
      }
      const extracted = isExperianResponseLayoutType(experianResponse) ? extractZipCodeFromResponseLayout(experianResponse) : extractZipCodeFromResponse(experianResponse);
      return Boolean(extracted) && extracted !== postalCode;
    };
    $[0] = experianResponse;
    $[1] = postalCode;
    $[2] = validationDetail?.zip_code_corrected;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isZipCodeMismatch = t1;
  let t2;
  if ($[4] !== experianResponse || $[5] !== isZipCodeMismatch || $[6] !== validationDetail?.zip_code_corrected) {
    t2 = isLocaleCA() ? !!experianResponse && validationDetail?.zip_code_corrected : isZipCodeMismatch();
    $[4] = experianResponse;
    $[5] = isZipCodeMismatch;
    $[6] = validationDetail?.zip_code_corrected;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const highlightZipCode = t2;
  const highlightStreet = experianResponse?.result?.confidence === "Premises partial" || experianResponse?.result?.confidence === "Street partial" || validationDetail?.street_corrected;
  let t3;
  if ($[8] !== addressName) {
    t3 = <p>{addressName}</p>;
    $[8] = addressName;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = highlightStreet && "sparky-u-color-utility-error";
  let t5;
  if ($[10] !== t4) {
    t5 = clsx(t4);
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== street || $[13] !== t5) {
    t6 = <p className={t5}>{street}</p>;
    $[12] = street;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== extendedAddress) {
    t7 = !!extendedAddress && <p>{extendedAddress}</p>;
    $[15] = extendedAddress;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = validationDetail?.city_name_corrected && "sparky-u-color-utility-error";
  let t9;
  if ($[17] !== t8) {
    t9 = clsx(t8);
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== city || $[20] !== t9) {
    t10 = <span className={t9}>{city}</span>;
    $[19] = city;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = validationDetail?.state_corrected && "sparky-u-color-utility-error";
  let t12;
  if ($[22] !== t11) {
    t12 = clsx(t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== state || $[25] !== t12) {
    t13 = <span className={t12}>{state}</span>;
    $[24] = state;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = highlightZipCode && "sparky-u-color-utility-error";
  let t15;
  if ($[27] !== t14) {
    t15 = clsx(t14);
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== postalCode || $[30] !== t15) {
    t16 = <span className={t15}>{postalCode}</span>;
    $[29] = postalCode;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t16) {
    t17 = <p>{t10},{" "}{t13}{" "}{t16}</p>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17 || $[37] !== t3 || $[38] !== t6 || $[39] !== t7) {
    t18 = <div data-testid="modal-item" className={componentClassname} data-cs-mask={true}>{t3}{t6}{t7}{t17}</div>;
    $[36] = t17;
    $[37] = t3;
    $[38] = t6;
    $[39] = t7;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}